import { defineStore } from "pinia";

import { MerchantApiError } from "../authentication/errors.js";
import {
  buildMonitoringPreferenceRules,
  classifyMonitoringMapping,
  defaultMonitoringFilters,
  defaultMonitoringSettingsDraft,
  settingsDraftFromMapping,
  type MonitoringFilters,
  type MonitoringMapping,
  type MonitoringObservation,
  type MonitoringPlanLimits,
  type MonitoringRunNowResult,
  type MonitoringSettingsDraft,
  type MonitoringSummary,
  type NotificationPreferenceRecord,
} from "./models.js";
import type { MonitoringService } from "./service.js";

export const useMonitoringStore = defineStore("merchant-monitoring", {
  state: (): {
    errorMessage?: string;
    filters: MonitoringFilters;
    loading: boolean;
    mappings: MonitoringMapping[];
    mutationMessage?: string;
    nextCursor: string | null;
    nextObservationCursor: string | null;
    observations: MonitoringObservation[];
    observationMonth: string;
    planLimits: MonitoringPlanLimits;
    preferences?: NotificationPreferenceRecord;
    runNowResult?: MonitoringRunNowResult;
    pendingCheckIds: string[];
    service?: MonitoringService;
    settingsDraft: MonitoringSettingsDraft;
    summary: MonitoringSummary["counts"];
  } => ({
    filters: defaultMonitoringFilters(),
    loading: false,
    mappings: [],
    pendingCheckIds: [],
    nextCursor: null,
    nextObservationCursor: null,
    observations: [],
    observationMonth: new Date().toISOString().slice(0, 7),
    planLimits: { maxMonitoredMappings: 0, allowedCadences: ["DAILY"] },
    settingsDraft: defaultMonitoringSettingsDraft(),
    summary: { totalVerified: 0, enabled: 0, due: 0, healthy: 0, failing: 0, changed: 0, stale: 0 },
  }),
  getters: {
    filteredMappings: (state): MonitoringMapping[] =>
      state.mappings.filter((mapping) => matchesFilters(mapping, state.filters)),
    limitExceeded: (state): boolean =>
      state.summary.enabled > state.planLimits.maxMonitoredMappings,
    limitUsageLabel: (state): string =>
      `${state.summary.enabled}/${state.planLimits.maxMonitoredMappings} monitored mappings`,
  },
  actions: {
    configure(service: MonitoringService): void {
      this.service = service;
    },
    async loadSummary(filters?: MonitoringFilters): Promise<void> {
      const effectiveFilters = filters ?? this.filters;
      this.filters = effectiveFilters;
      this.loading = true;
      delete this.errorMessage;
      try {
        const [summary, preferences] = await Promise.all([
          this.requireService().loadSummary(effectiveFilters),
          this.requireService().loadNotificationPreferences(),
        ]);
        this.applySummary(summary);
        this.preferences = preferences;
      } catch (error: unknown) {
        this.errorMessage = messageForError(error, "Monitoring summary could not be loaded.");
      } finally {
        this.loading = false;
      }
    },
    async loadMore(): Promise<void> {
      if (this.nextCursor === null) return;
      this.loading = true;
      delete this.errorMessage;
      try {
        const summary = await this.requireService().loadSummary({
          ...this.filters,
          cursor: this.nextCursor,
        });
        this.mappings = [...this.mappings, ...summary.mappings];
        this.summary = summary.counts;
        this.planLimits = summary.planLimits;
        this.nextCursor = summary.nextCursor;
      } catch (error: unknown) {
        this.errorMessage = messageForError(error, "More monitoring mappings could not be loaded.");
      } finally {
        this.loading = false;
      }
    },
    async loadObservationHistory(mapping: MonitoringMapping, month?: string): Promise<void> {
      const effectiveMonth = month ?? this.observationMonth;
      this.observationMonth = effectiveMonth;
      delete this.errorMessage;
      try {
        const page = await this.requireService().loadObservationHistory(
          mapping.mappingId,
          mapping.platformProductId,
          effectiveMonth,
          25,
        );
        this.observations = page.observations;
        this.nextObservationCursor = page.nextCursor;
      } catch (error: unknown) {
        this.errorMessage = messageForError(error, "Observation history could not be loaded.");
      }
    },
    async loadMoreObservations(mapping: MonitoringMapping): Promise<void> {
      if (this.nextObservationCursor === null) return;
      try {
        const page = await this.requireService().loadObservationHistory(
          mapping.mappingId,
          mapping.platformProductId,
          this.observationMonth,
          25,
          this.nextObservationCursor,
        );
        this.observations = [...this.observations, ...page.observations];
        this.nextObservationCursor = page.nextCursor;
      } catch (error: unknown) {
        this.errorMessage = messageForError(error, "More observations could not be loaded.");
      }
    },
    editGlobalSettings(enabled: boolean): void {
      this.settingsDraft = { ...defaultMonitoringSettingsDraft(), enabled };
    },
    editMappingSettings(mapping: MonitoringMapping): void {
      this.settingsDraft = settingsDraftFromMapping(mapping);
    },
    async saveSettings(): Promise<void> {
      delete this.errorMessage;
      delete this.mutationMessage;
      try {
        const result = await this.requireService().saveSettings(this.settingsDraft);
        if (Array.isArray((result as MonitoringSummary).mappings)) {
          this.applySummary(result as MonitoringSummary);
        } else {
          this.applyMapping(result as MonitoringMapping);
        }
        await this.saveAlertPreferencesAfterSettingsChanged();
      } catch (error: unknown) {
        this.errorMessage = messageForError(error, "Monitoring settings could not be saved.");
      }
    },
    async runNow(mapping: MonitoringMapping): Promise<void> {
      if (this.pendingCheckIds.includes(mapping.mappingId)) return;
      this.pendingCheckIds.push(mapping.mappingId);
      delete this.errorMessage;
      try {
        this.runNowResult = await this.requireService().runNow(
          mapping.mappingId,
          mapping.platformProductId,
        );
        this.mutationMessage = `Check ${this.runNowResult.jobId}: ${this.runNowResult.status}. Queue acceptance is not a completed check.`;
      } catch (error: unknown) {
        this.errorMessage = messageForError(
          error,
          "The check submission is unconfirmed. Retry to reconcile the same operation.",
        );
      } finally {
        this.pendingCheckIds = this.pendingCheckIds.filter((id) => id !== mapping.mappingId);
      }
    },
    async saveAlertPreferences(): Promise<void> {
      const base = this.preferences ?? {
        rules: [],
        updatedAt: new Date(0).toISOString(),
        updatedBy: "local",
      };
      this.preferences = await this.requireService().saveNotificationPreferences({
        ...base,
        rules: mergeMonitoringRules(base.rules, buildMonitoringPreferenceRules(this.settingsDraft)),
      });
    },
    async saveAlertPreferencesAfterSettingsChanged(): Promise<void> {
      try {
        await this.saveAlertPreferences();
        this.mutationMessage = "Monitoring settings saved.";
      } catch {
        this.mutationMessage =
          "Monitoring settings saved, but alert preferences could not be updated.";
      }
    },
    applySummary(summary: MonitoringSummary): void {
      this.mappings = summary.mappings;
      this.summary = summary.counts;
      this.planLimits = summary.planLimits;
      this.nextCursor = summary.nextCursor;
    },
    applyMapping(mapping: MonitoringMapping): void {
      this.mappings = this.mappings.map((item) =>
        item.mappingId === mapping.mappingId ? mapping : item,
      );
      if (!this.mappings.some((item) => item.mappingId === mapping.mappingId))
        this.mappings = [mapping, ...this.mappings];
      this.recalculateSummary();
    },
    recalculateSummary(): void {
      this.summary = this.mappings.reduce(
        (counts, mapping) => {
          const health = classifyMonitoringMapping(mapping);
          return {
            totalVerified: counts.totalVerified + 1,
            enabled: counts.enabled + (mapping.monitoringEnabled ? 1 : 0),
            due: counts.due + (health === "DUE" ? 1 : 0),
            healthy: counts.healthy + (health === "HEALTHY" ? 1 : 0),
            failing: counts.failing + (health === "FAILING" ? 1 : 0),
            changed: counts.changed + (health === "CHANGED" ? 1 : 0),
            stale: counts.stale + (health === "STALE" ? 1 : 0),
          };
        },
        { totalVerified: 0, enabled: 0, due: 0, healthy: 0, failing: 0, changed: 0, stale: 0 },
      );
    },
    requireService(): MonitoringService {
      if (this.service === undefined) {
        throw new Error("Monitoring store was not configured before use.");
      }
      return this.service;
    },
  },
});

export { classifyMonitoringMapping, defaultMonitoringFilters };

function matchesFilters(mapping: MonitoringMapping, filters: MonitoringFilters): boolean {
  const health = classifyMonitoringMapping(mapping);
  return [
    filters.health === undefined || filters.health === "" || health === filters.health,
    filters.productId === undefined || mapping.platformProductId === filters.productId,
    filters.mappingId === undefined || mapping.mappingId === filters.mappingId,
    filters.competitorDomain === undefined || mapping.competitorDomain === filters.competitorDomain,
    filters.currency === undefined || mapping.latestCurrency === filters.currency,
  ].every(Boolean);
}

function mergeMonitoringRules(
  existing: NotificationPreferenceRecord["rules"],
  next: NotificationPreferenceRecord["rules"],
): NotificationPreferenceRecord["rules"] {
  const nextKeys = new Set(
    next.map((rule) => `${rule.eventType}:${rule.channel}:${rule.scope ?? ""}`),
  );
  return [
    ...existing.filter(
      (rule) => !nextKeys.has(`${rule.eventType}:${rule.channel}:${rule.scope ?? ""}`),
    ),
    ...next,
  ];
}

function messageForError(error: unknown, fallback: string): string {
  if (error instanceof MerchantApiError && error.kind === "access")
    return "You do not have permission to manage monitoring.";
  if (error instanceof MerchantApiError && error.kind === "validation") return error.message;
  if (error instanceof Error) return error.message;
  return fallback;
}
